import { canRequestMoreInfo } from '@/domains/jobs/constants/canRequestMoreInfo';
import type { ConversationMessage } from '@/domains/jobs/types/workOrder';
import { Badge } from '@/shared/ui/badge';
import { CheckCheck, MessageSquare, Reply } from 'lucide-react';
import { AwaitingReplyBadge } from './AwaitingReplyBadge';
import { ResponseReceivedBadge } from './ResponseReceivedBadge';

interface MirStatusBadgeProps {
  mirPendingDirection: 'inbound' | 'outbound' | null | undefined;
  mirConversationHistory?: ConversationMessage[];
  /** Current work-order status; the MIR badge is hidden on terminal states. */
  workOrderStatus: string | null | undefined;
}

export function MirStatusBadge({
  mirPendingDirection,
  mirConversationHistory,
  workOrderStatus,
}: MirStatusBadgeProps) {
  // Terminal work orders (Completed/Rejected/Cancelled/Validated) have no live
  // MIR, so hide the badge entirely — mirrors RequestMoreInfoButton (WRK-5644).
  // Without this the history fallback below would resurrect "Awaiting reply" on
  // a cancelled Duration Challenged job whose last message is a request-info
  // (WRK-5571).
  if (!canRequestMoreInfo(workOrderStatus ?? '')) return null;

  // null case — derive from conversation history
  const latest = mirConversationHistory?.[0];

  if (mirPendingDirection === 'inbound') {
    return (
      <Badge className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 hover:bg-amber-500/20 gap-1 text-nowrap">
        <Reply className="size-3 shrink-0" />
        Reply Required
      </Badge>
    );
  }
  if (mirPendingDirection === 'outbound') {
    return <AwaitingReplyBadge mirPendingDirection="outbound" />;
  }

  if (latest) {
    if (latest.type === 'response' && latest.sender === 'core') {
      return <ResponseReceivedBadge hasResponse={true} />;
    }
    if (latest.type === 'response' && latest.sender === 'skewbworks') {
      return (
        <Badge className="bg-teal-500/10 text-teal-700 dark:text-teal-400 border-teal-500/30 hover:bg-teal-500/20 gap-1 text-nowrap">
          <CheckCheck className="size-3 shrink-0" />
          Reply Sent
        </Badge>
      );
    }
    return <AwaitingReplyBadge mirPendingDirection="outbound" />;
  }
  return (
    <Badge className="bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/30 hover:bg-slate-500/20 gap-1 text-nowrap">
      <MessageSquare className="size-3 shrink-0" />
      No conversation started
    </Badge>
  );
}
